import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { WhiteLabelForm } from '../../components/admin/WhiteLabelForm';
import { useAuth } from '../../contexts/AuthContext';
import { getPricingPlans, getWhiteLabelById, updateWhiteLabel } from '../../lib/api';
import { toast } from 'sonner';
import {
  WHITE_LABEL_FEATURE_FIELDS as FEATURE_FIELDS,
  WHITE_LABEL_DEFAULT_FORM_VALUES,
  buildWhiteLabelUpdatePayload,
  editWhiteLabelFormSchema,
  getWhiteLabelErrorMessage,
  mapWhiteLabelApiErrors,
  mapWhiteLabelToFormValues,
} from '../../lib/whiteLabelValidation';

const extractPricingPlans = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.data?.items)) return resp.data.items;
  return [];
};

const normalizeStatusLabel = (status) => {
  const val = String(status || '').toLowerCase();
  if (val === 'inactive') return 'Inactive';
  if (val === 'suspended') return 'Suspended';
  return 'Active';
};

const mapWhiteLabel = (raw) => ({
  id: raw?.id || '',
  brandName: raw?.brand_name || '',
  ownerName: raw?.owner_name || '',
  adminName: raw?.admin_name || '',
  adminEmail: raw?.admin_email || '',
  phone: raw?.phone || '',
  domain: raw?.custom_domain || '',
  primaryColor: raw?.primary_color || '#6366f1',
  secondaryColor: raw?.secondary_color || '#8b5cf6',
  commissionPlan: raw?.commission_plan || 'standard',
  pricingPlanId: raw?.pricing_plan_id || '',
  pricingPlanName: raw?.pricing_plan_name || '',
  status: String(raw?.status || 'active').toLowerCase(),
  featurePermissions: raw?.feature_permissions || {},
  agents: raw?.counts?.agents ?? 0,
  leads: raw?.counts?.leads ?? 0,
  createdAt: raw?.created_at,
  updatedAt: raw?.updated_at,
});

const toDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

export const ViewWhiteLabel = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!token || !id) return;
      setLoading(true);
      try {
        const resp = await getWhiteLabelById(token, id);
        setData(mapWhiteLabel(resp?.data));
      } catch (err) {
        toast.error(err?.message || 'Failed to load white label details');
        navigate('/admin/whitelabels');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, id, navigate]);

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading white label details...</div>;
  }
  if (!data) return null;

  const statusLabel = normalizeStatusLabel(data.status);

  return (
    <div className="space-y-5 max-w-7xl w-full">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Globe className="h-6 w-6 text-purple-500" />
          <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">White Label Details</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/whitelabels')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => navigate(`/admin/whitelabels/${data.id}/edit`)}>
            Edit White Label
          </Button>
        </div>
      </div>

      <div className="rounded-xl border p-6 shadow-sm space-y-5">
        <h2 className="font-semibold text-base border-b pb-3">Brand Information</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <div><span className="text-muted-foreground">Brand Name:</span><div className="font-medium">{data.brandName || '—'}</div></div>
          <div><span className="text-muted-foreground">Owner Name:</span><div className="font-medium">{data.ownerName || '—'}</div></div>
          <div><span className="text-muted-foreground">WL Admin Name:</span><div className="font-medium">{data.adminName || '—'}</div></div>
          <div><span className="text-muted-foreground">WL Admin Email:</span><div className="font-medium">{data.adminEmail || '—'}</div></div>
          <div><span className="text-muted-foreground">WL Admin Password:</span><div className="font-medium">******** (hidden)</div></div>
          <div><span className="text-muted-foreground">Phone Number:</span><div className="font-medium">{data.phone || '—'}</div></div>
          <div className="sm:col-span-2"><span className="text-muted-foreground">Custom Domain:</span><div className="font-medium">{data.domain || '—'}</div></div>
        </div>

        <h2 className="font-semibold text-base border-b pb-3 pt-2">Branding & Plan</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Primary Color:</span>
            <div className="mt-1 flex items-center gap-2 font-medium"><span className="h-4 w-4 rounded border" style={{ backgroundColor: data.primaryColor }} />{data.primaryColor}</div>
          </div>
          <div>
            <span className="text-muted-foreground">Secondary Color:</span>
            <div className="mt-1 flex items-center gap-2 font-medium"><span className="h-4 w-4 rounded border" style={{ backgroundColor: data.secondaryColor }} />{data.secondaryColor}</div>
          </div>
          <div><span className="text-muted-foreground">Commission Plan:</span><div className="font-medium">{data.commissionPlan}</div></div>
          <div><span className="text-muted-foreground">Pricing Plan:</span><div className="font-medium">{data.pricingPlanName || 'No plan selected'}</div></div>
          <div><span className="text-muted-foreground">Status:</span><div className="font-medium">{statusLabel}</div></div>
        </div>

        <h2 className="font-semibold text-base border-b pb-3 pt-2">Feature Permissions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {FEATURE_FIELDS.map((item) => (
            <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border">
              <span className="text-sm font-medium">{item.label}</span>
              <span className={`text-xs font-semibold ${data.featurePermissions[item.key] ? 'text-emerald-600' : 'text-red-500'}`}>
                {data.featurePermissions[item.key] ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          ))}
        </div>

        <h2 className="font-semibold text-base border-b pb-3 pt-2">System Information</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div><span className="text-muted-foreground">WL ID:</span><div className="font-medium break-all">{data.id}</div></div>
          <div><span className="text-muted-foreground">Agents:</span><div className="font-medium">{data.agents}</div></div>
          <div><span className="text-muted-foreground">Leads:</span><div className="font-medium">{data.leads}</div></div>
          <div><span className="text-muted-foreground">Created At:</span><div className="font-medium">{toDateTime(data.createdAt)}</div></div>
          <div className="sm:col-span-2"><span className="text-muted-foreground">Updated At:</span><div className="font-medium">{toDateTime(data.updatedAt)}</div></div>
        </div>
      </div>
    </div>
  );
};

export const EditWhiteLabel = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [submitError, setSubmitError] = useState('');
  const form = useForm({
    resolver: zodResolver(editWhiteLabelFormSchema),
    defaultValues: {
      ...WHITE_LABEL_DEFAULT_FORM_VALUES,
      adminPassword: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    const load = async () => {
      if (!token || !id) return;
      setLoading(true);
      try {
        const [wlResp, plansResp] = await Promise.all([
          getWhiteLabelById(token, id),
          getPricingPlans(token),
        ]);
        const wl = mapWhiteLabel(wlResp?.data);
        form.reset({
          ...mapWhiteLabelToFormValues(wl),
          adminPassword: '',
        });
        setSubmitError('');
        setPricingPlans(extractPricingPlans(plansResp));
      } catch (err) {
        toast.error(err?.message || 'Failed to load white label');
        navigate('/admin/whitelabels');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, id, navigate, form]);

  useEffect(() => {
    const subscription = form.watch(() => {
      if (submitError) {
        setSubmitError('');
      }
    });
    return () => subscription.unsubscribe();
  }, [form, submitError]);

  const handleSubmit = async (values) => {
    if (!token || !id) return;

    setSubmitError('');
    setSaving(true);
    try {
      const payload = buildWhiteLabelUpdatePayload(values, pricingPlans);
      await updateWhiteLabel(token, id, payload);
      toast.success('White label updated successfully');
      navigate(`/admin/whitelabels/${id}/view`);
    } catch (err) {
      const fieldErrors = mapWhiteLabelApiErrors(err, 'edit');
      Object.entries(fieldErrors).forEach(([fieldName, message]) => {
        form.setError(fieldName, { type: 'server', message });
      });
      const firstField = Object.keys(fieldErrors)[0];
      if (firstField) {
        form.setFocus(firstField);
      }

      const message = getWhiteLabelErrorMessage(err, 'Failed to update white label.');
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading white label details...</div>;
  }

  return (
    <div className="space-y-5 max-w-7xl w-full">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Globe className="h-6 w-6 text-purple-500" />
          <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">Edit White Label</h1>
        </div>
        <Button variant="outline" onClick={() => navigate(`/admin/whitelabels/${id}/view`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to View
        </Button>
      </div>

      <WhiteLabelForm
        form={form}
        mode="edit"
        pricingPlans={pricingPlans}
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/admin/whitelabels/${id}/view`)}
        submitLabel="Save Changes"
        submitting={saving}
        submitError={submitError}
      />
    </div>
  );
};
